import React, { useState } from "react";
import { useCreateNewUserConversationMutation } 
  from "@/features/userConversations/userConversationsApiSlice";

import { buildUserConversationPayload } 
  from "@/features/userConversations/buildUserConversationPayload";

import EtContentEditorLight2 from "@/utils/EtContentEditorLight2";


const EtUpdateUserConversationLandingComp = ({
  userId,
  userOrgId,

  conversationMaster,     // 👈 canonical record
  weekStarting,

  onClose = () => {},
  refetchUserConversations = () => {},
  allUsers=[],      // 06 Feb 2026
}) => {

  // First Created - 23 Jan 2026
  // Author - Atul Pandey
  // Purpose - Update / delete a single UserConversation note (Contact or All). Uses payload-driven backend contract (no mode flags)
  // Updated - 06 Feb 2026 - added allUsers prop to support sharedWith feature 

  // console.log('allUsers in EtUpdateUserConversationLandingComp :', allUsers)

  // ------------------------------------------------------------------
  // 1️⃣ Derive authoritative context from master
  // ------------------------------------------------------------------
  const {
    sourceConversation,
    conversationText,
    contactId,
    trailId,
    sessionId,
    selectedFocus,
    connectedEntities = [],
  } = conversationMaster;

  const {
    sourceNoteType, // 'Contact' | 'All'
    sourceNoteId,
  } = sourceConversation;

  // ------------------------------------------------------------------
  // 2️⃣ Local editable state (skeleton)
  // ------------------------------------------------------------------
  const [noteText, setNoteText] = useState(conversationText || "");

  const [markAsMagicMoment, setMarkAsMagicMoment] = useState(
    !!conversationMaster?.userMagicMomentId
  );

  // ⭐ 06 Feb 2026 — SharedWith state (reflection-level)
  const [sharedWith, setSharedWith] = React.useState(
    conversationMaster?.sharedWith || []
  );

  // ⭐ 06 Feb 2026 - Shared With (Conversation-level)
  const shareableUsers = React.useMemo(() => {
    return allUsers.filter(u =>
      u._id !== conversationMaster.userId?._id   // ❌ cannot share with self
    );
  }, [allUsers, conversationMaster]);

  // ⭐ 06 Feb 2026 - Handle Add SharedWith user
  const handleAddSharedUser = (userId) => {
    if (!userId) return;

    setSharedWith(prev => {
      const exists = prev.some(
        sw => (sw.userId?._id || sw.userId) === userId
      );
      if (exists) return prev;

      return [...prev, { userId }];
    });
  };

  // ⭐ 06 Feb 2026 - Handle Remove SharedWith user
  const handleRemoveSharedUser = (userId) => {
    setSharedWith(prev =>
      prev.filter(
        sw => (sw.userId?._id || sw.userId) !== userId
      )
    );
  };

  // ⭐ 06 Feb 2026 - Add ALL users at once
  const handleAddAllSharedUsers = () => {
    const allShareableIds = shareableUsers.map(u => u._id);

    setSharedWith(prev => {
      const existingIds = prev.map(
        sw => sw.userId?._id || sw.userId
      );

      const newEntries = allShareableIds
        .filter(id => !existingIds.includes(id))
        .map(id => ({ userId: id }));

      return [...prev, ...newEntries];
    });
  };

  // ⭐ 06 Feb 2026 - Remove ALL users at once
  const handleRemoveAllSharedUsers = () => {
    setSharedWith([]);
  };

  //   const [markAsMagicMoment] = useState(
//     !!conversationMaster?.userMagicMomentId
//   );

  // ------------------------------------------------------------------
  // 3️⃣ Mutation
  // ------------------------------------------------------------------
  const [createNewUserConversation, { isLoading }] =
    useCreateNewUserConversationMutation();

  // ------------------------------------------------------------------
  // 4️⃣ Save (UPDATE)
  // ------------------------------------------------------------------
  const handleSave = async () => {
    try {
      const notePayload =
        sourceNoteType === "Contact"
          ? {
              _id: sourceNoteId,
              userContactId: contactId?._id || contactId,
              userConversationContactNoteText: noteText,
              userConversationContactNoteUpdateDate: new Date(),
            }
          : {
              _id: sourceNoteId,
              userConversationAllNoteText: noteText,
              userConversationAllNoteUpdateDate: new Date(),
            };

      const payload = buildUserConversationPayload({
        userId,
        userOrgId,
        weekStarting,
        noteType: sourceNoteType,
        note: notePayload,
        context: {
          trailId,
          sessionId,
          selectedFocus,
          connectedEntities,
        },
      });

      payload.sharedWith = sharedWith // ⭐ 06 Feb 2026 — conversation-level sharing
      await createNewUserConversation(payload).unwrap();

      refetchUserConversations();
      onClose();
    } catch (err) {
      console.error("Failed to update conversation note:", err);
      alert("Failed to update conversation");
    }
  };

  // ------------------------------------------------------------------
  // 5️⃣ Delete
  // ------------------------------------------------------------------
  const handleDelete = async () => {
    try {
      const payload = buildUserConversationPayload({
        userId,
        userOrgId,
        weekStarting,
        noteType: sourceNoteType,
        note: {
          _id: sourceNoteId,
          delete: true,
        },
        context: {
          trailId,
          sessionId,
          selectedFocus,
          connectedEntities,
        },
      });

      await createNewUserConversation(payload).unwrap();

      refetchUserConversations();
      onClose();
    } catch (err) {
      console.error("Failed to delete conversation note:", err);
      alert("Failed to delete conversation");
    }
  };

  // ------------------------------------------------------------------
  // 6️⃣ Skeleton UI (placeholder)
  // ------------------------------------------------------------------
  return (
    <div className="p-3 bg-gray-50 rounded">

      <p className="text-sm font-bold mb-2">
        Update Conversation
      </p>

      <p className="text-xs text-gray-600 mb-2">
        {contactId
           ? `With ${contactId?.contactName || "Contact"}${
                contactId?.contactTypeId?.contactTypeName
                ? ` (${contactId.contactTypeId.contactTypeName})`
                : ""
            }`
            : "With Self"}
        </p>

      <EtContentEditorLight2
        value={noteText}
        handleChange={setNoteText}
        placeholderText="Update your conversation..."
        editorKey={`update-conversation-note-${sourceNoteId}`}
      />

      {/* Magic Moment */}
      <div className="flex items-center space-x-2 mt-2">
        <input
            type="checkbox"
            id="markAsMagicMoment"
            checked={markAsMagicMoment}
            onChange={() => setMarkAsMagicMoment(prev => !prev)}
            className="w-4 h-4"
        />
        <label
            htmlFor="markAsMagicMoment"
            className="text-xs text-gray-700 font-medium"
        >
            Mark this as a Magic Moment
        </label>
      </div>

      {/* --------------------------------- */}
      {/* 06 Feb 2026 - Shared With Section */}
      {/* --------------------------------- */}
      <div className="mt-4">

          <div className='flex justify-start items-center flex-wrap'>
              <p className="text-xs font-semibold text-gray-700 m-1">
                Share this reflection with
              </p>

              {/* Add user dropdown */}
              <select
                className="border rounded px-2 py-1 text-xs"
                defaultValue=""
                onChange={(e) => {
                  handleAddSharedUser(e.target.value);
                  e.target.value = "";
                }}
              >
                <option value="" disabled>
                  Add user from your org
                </option>
                {shareableUsers
                  .filter(u =>
                    !sharedWith.some(
                      sw => (sw.userId?._id || sw.userId) === u._id
                    )
                  )
                  .map(user => (
                    <option key={user._id} value={user._id}>
                      {user.name || user.username}
                    </option>
                  ))}
              </select>

              {/* Bulk actions */}
              <div className="flex items-center mx-2 space-x-2">
                {/* Select All — show only if NOT all users are selected */}
                {sharedWith.length < shareableUsers.length && (
                  <button
                    type="button"
                    onClick={handleAddAllSharedUsers}
                    className="text-xs text-white bg-lime-500 px-2 py-1 hover:underline rounded"
                  >
                    Select All
                  </button>
                )}

                {/* Remove All — show only if at least one user is selected */}
                {sharedWith.length > 0 && (
                  <button
                    type="button"
                    onClick={handleRemoveAllSharedUsers}
                    className="text-xs text-white bg-red-500 px-2 py-1 hover:underline rounded"
                    disabled={sharedWith.length === 0}
                  >
                    Remove All
                  </button>
                )}
              </div>

          </div>

          {/* Selected users */}
          <div className="flex flex-wrap gap-2 my-2 py-2">
            {sharedWith.map(sw => {
              const uid = sw.userId?._id || sw.userId;
              const user = allUsers.find(u => u._id === uid);

              if (!user) return null;

              return (
                <span
                  key={uid}
                  className="flex items-center bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs"
                >
                  {user.name || user.username}
                  <button
                    onClick={() => handleRemoveSharedUser(uid)}
                    className="ml-1 text-orange-600 hover:text-orange-800"
                  >
                    ✕
                  </button>
                </span>
              );
            })}
          </div>
      </div>

      

      <div className="flex gap-2 mt-3">
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="px-3 py-1 text-xs text-white bg-lime-600 rounded"
        >
          Save
        </button>

        {/* <button
          onClick={handleDelete}
          disabled={isLoading}
          className="px-3 py-1 text-xs text-white bg-red-600 rounded"
        >
          Delete
        </button> */}

        <button
          onClick={onClose}
          className="px-3 py-1 text-xs text-white bg-gray-400 rounded"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default EtUpdateUserConversationLandingComp;